import { ChangeDetectorRef, Component, ElementRef, Host, HostBinding, Input, OnDestroy, Optional, Renderer2 } from '@angular/core';
import { NgModel, ValidationErrors } from '@angular/forms';
import { NOTICES } from '@df/ui/api/notice/notice.const';
import { getValidatePatternErrorMessage } from '@df/ui/form/validate-pattern.config';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { Subscription } from 'rxjs';
import { InputWrapComponent } from './input-wrap.component';

@Component({
    selector: 'validation',
    templateUrl: './validation.html',
    standalone: true,
    imports: [TranslationModule]
})
export class ValidationComponent implements OnDestroy {
    /**
     * Custom error messages passed via attr
     */
    @Input()
    messages?: { [error: string]: string };

    /**
     * Custom error message for all errors
     */
    @Input()
    message?: string;

    /**
     * Manual can show setter
     */
    @Input()
    set canShow(canShow: boolean | undefined) {
        this._canShow = canShow;
    }

    /**
     * Manual can show setter
     */
    get canShow(): boolean {
        if (this._canShow !== undefined) {
            return this._canShow;
        }

        return !!this.ngModel?.formDirective?.submitAttempt;
    }

    protected _ngModel?: NgModel;

    protected _canShow?: boolean;

    protected _errors: ValidationErrors | undefined;

    protected subscriptions = new Subscription();

    constructor(
        protected changeDetectionRef: ChangeDetectorRef,
        protected elementRef: ElementRef,
        protected renderer2: Renderer2,
        @Optional() @Host() protected inputWrapComponent: InputWrapComponent
    ) {}

    ngOnInit(): void {
        this.subscriptions.add(
            this.ngModel?.statusChanges!.subscribe(value => {
                this._errors = this.ngModel?.errors || undefined;

                this.detectChanges();
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions?.unsubscribe();
    }

    ngAfterViewInit() {
        [
            this.ngModel?.control?.valueChanges.subscribe(() => this.toggleClass()),
            this.ngModel?.formDirective?.ngSubmit.subscribe(() => this.toggleClass())
        ].forEach(i => i && this.subscriptions.add(i));
    }
    toggleClass() {
        this.isHidden
            ? this.renderer2.addClass(this.elementRef.nativeElement, 'ng-hide')
            : this.renderer2.removeClass(this.elementRef.nativeElement, 'ng-hide');
    }

    /**
     * Manual ng model setter
     */
    set ngModel(ngModel: NgModel | undefined) {
        this._ngModel = ngModel;
    }

    /**
     * Ng model getter
     * priority has manually set one
     * if we are child of input wrap then we will pull it automatically
     */
    get ngModel(): NgModel | undefined {
        if (this._ngModel) {
            return this._ngModel;
        }

        return this.inputWrapComponent?.body?.ngModel;
    }

    /**
     * Ng hide class setter on component
     */
    @HostBinding('class.ng-hide')
    get isHidden(): boolean {
        return !this.canShow || !this.hasErrors;
    }

    @HostBinding('class.is-pending')
    get pending(): boolean {
        return this.ngModel?.pending || false;
    }

    get hasErrors(): boolean {
        return !!this.errors;
    }

    get errors(): ValidationErrors | undefined {
        return this._errors || this.ngModel?.errors || undefined;
    }

    get errorMessage(): string | undefined {
        return this.getErrorMessage();
    }

    /**
     * Gets errors object
     */
    getErrors() {
        return this.errors;
    }

    /**
     * Gets error message
     */
    getErrorMessage(): string | undefined {
        const errors = this.errors;

        if (!errors) {
            return undefined;
        }

        if (this.message) {
            return this.message;
        }
        const errorsNames = Object.keys(errors);

        if (errorsNames.length) {
            if (this.messages) {
                const customErrorName = errorsNames.find(errorName => typeof this.messages?.[errorName] !== 'undefined');

                if (customErrorName) {
                    const customError = this.messages[customErrorName];

                    if (typeof customError !== 'undefined') {
                        return customError;
                    }
                }
            }

            let validatePatternErrorMessage: string | undefined;

            errorsNames.find(errorName => {
                const error = getValidatePatternErrorMessage(errorName);

                if (error) {
                    validatePatternErrorMessage = error;

                    return true;
                }

                const errorInfo = errors[errorName];
                if (errorInfo && typeof errorInfo === 'object' && errorInfo.message) {
                    validatePatternErrorMessage = errorInfo.message;

                    return true;
                }
                return false;
            });

            if (validatePatternErrorMessage) {
                return validatePatternErrorMessage;
            }
        }

        if (errors['email']) {
            return NOTICES['INVLIDEMAILFIELD'];
        }

        if (errors['required']) {
            return NOTICES['REQUIREDFIELD'];
        }

        if (errors['validateEqual']) {
            return NOTICES['NOTMATCHED'];
        }

        if (errors['emojiDetected']) {
            return NOTICES['EMOJIDETECTED'];
        }

        return NOTICES[Object.keys(errors)[0]] || NOTICES['INVALIDFIELD'];
    }

    detectChanges(): void {
        this.inputWrapComponent?.detectChanges();
    }
}
